import React, { useState } from 'react';
import { Card, Form, Input, Button, Alert, Typography } from 'antd';
import { LockOutlined, SafetyOutlined } from '@ant-design/icons';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const { Text } = Typography;

/**
 * 强制改密页：
 *  - 由 Login 页在收到 require_password_change 后跳转过来
 *  - 用户提交新密码（需满足 8 位 + 字母 + 数字）
 *  - 后端校验通过后签发正式 token，跳转主页
 *  - 任何时候点 "返回登录" 都可以放弃，回到 /admin/login
 */
export default function ChangePassword() {
  const { user, pendingChange, submitPasswordChange, cancelPasswordChange } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // 没有改密握手态（直接访问此 URL）→ 强制回登录页
  if (!pendingChange || !pendingChange.changeToken) {
    return <Navigate to="/admin/login" replace />;
  }
  // 已经登录了 → 跳主页
  if (user) {
    const dest = location.state?.from || '/';
    return <Navigate to={dest} replace />;
  }

  const onFinish = async (values) => {
    setErrorMsg('');
    if (values.new_password !== values.confirm_password) {
      setErrorMsg('两次输入的密码不一致');
      return;
    }
    setLoading(true);
    try {
      await submitPasswordChange(values.new_password);
      // 改密成功，由 AuthContext 写入 token 和 user，跳到主页
      navigate('/', { replace: true });
    } catch (e) {
      setErrorMsg(e?.message || '修改密码失败');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    cancelPasswordChange();
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="login-page">
      <Card
        title={
          <span>
            <SafetyOutlined style={{ marginRight: 8 }} />
            设置新密码
          </span>
        }
        className="login-card"
        styles={{ header: { textAlign: 'center', fontSize: 18 } }}
      >
        <Alert
          type="warning"
          showIcon
          message={pendingChange.message || '出于安全考虑，请先设置一个新的登录密码'}
          description={
            <Text>
              账号：<b>{pendingChange.user?.username}</b>
              <br />
              改密凭证 15 分钟内有效，逾期请重新登录。
            </Text>
          }
          style={{ marginBottom: 16 }}
        />
        {errorMsg && <Alert type="error" message={errorMsg} style={{ marginBottom: 12 }} />}
        <Form name="change-password" onFinish={onFinish} autoComplete="off" size="large" layout="vertical">
          <Form.Item
            name="new_password"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 8, message: '密码至少 8 位' },
              {
                pattern: /^(?=.*[A-Za-z])(?=.*\d).+$/,
                message: '须同时包含字母和数字',
              },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="至少 8 位，含字母和数字" autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label="确认新密码"
            dependencies={['new_password']}
            rules={[
              { required: true, message: '请再次输入新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="再次输入新密码" autoComplete="new-password" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              提交并登录
            </Button>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'center' }}>
            <Button type="link" onClick={handleBack}>
              返回登录
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}